import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {HTMLElement | null} */
  #returnFocusElement = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.addEventListener(DialogCloseEvent.eventName, this.#resetAddedStateAndRestoreFocus);
    this.addEventListener('cart-items:discount-update-complete', this.#handleDiscountUpdateComplete);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.removeEventListener(DialogCloseEvent.eventName, this.#resetAddedStateAndRestoreFocus);
    this.removeEventListener('cart-items:discount-update-complete', this.#handleDiscountUpdateComplete);
    this.#historyAbortController?.abort();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Handles successful product-form cart additions. Quantity changes and failed additions keep their native behavior
   * without showing the confirmation treatment.
   * @param {CustomEvent<{ resource?: { item_count?: number }, sourceId?: string, data?: { didError?: boolean, source?: string } }>} event
   */
  #handleCartAdd = (event) => {
    const isSuccessfulProductAdd =
      event.detail.data?.source === 'product-form-component' && event.detail.data?.didError !== true;

    if (!isSuccessfulProductAdd) return;

    const activeElement = document.activeElement;
    if (activeElement instanceof HTMLElement) {
      this.#returnFocusElement = activeElement;
    }

    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    this.#announceCartCount(event.detail.resource?.item_count);

    const variantId = event.detail.sourceId;
    const returnFocusElement = this.#returnFocusElement;

    // The cart-items component morphs the header section in response to this same event. Wait for that native
    // render to settle, then decorate the current drawer instance instead of writing state into stale markup.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const currentDrawer = document.querySelector('cart-drawer-component');
        if (!(currentDrawer instanceof CartDrawerComponent)) return;

        currentDrawer.#showAddedState({ variantId, returnFocusElement });
      });
    });
  };

  /**
   * Shows the transient confirmation state after the server-rendered cart markup has settled.
   * @param {{ variantId?: string, returnFocusElement: HTMLElement | null }} details
   */
  #showAddedState({ variantId, returnFocusElement }) {
    this.#returnFocusElement = returnFocusElement;

    const confirmation = this.querySelector('[data-cart-added-confirmation]');
    confirmation?.removeAttribute('hidden');

    this.querySelectorAll('.cart-items__table-row--just-added').forEach((row) => {
      row.classList.remove('cart-items__table-row--just-added');
    });

    if (variantId) {
      const addedRow = this.querySelector(`[data-key^="${CSS.escape(variantId)}:"]`);
      if (addedRow instanceof HTMLElement) {
        addedRow.classList.add('cart-items__table-row--just-added');
      }
    }

    if (this.hasAttribute('auto-open') && !this.refs.dialog?.open) {
      this.showDialog();
    }

    this.#resetContentScroll();

    this.#updateStickyState();
  }


  /** Recomputes the sticky summary state after Fabric completes a discount-driven section morph. */
  #handleDiscountUpdateComplete = () => {
    if (!this.refs.dialog?.open) return;

    this.#updateStickyState();
  };


  /** Resets the Phase 3C primary drawer scroller without introducing a second scrolling surface. */
  #resetContentScroll() {
    const content = this.querySelector('.cart-drawer__content');
    if (content instanceof HTMLElement) content.scrollTop = 0;
  }


  #resetAddedStateAndRestoreFocus = () => {
    const confirmation = this.querySelector('[data-cart-added-confirmation]');
    confirmation?.setAttribute('hidden', '');

    this.querySelectorAll('.cart-items__table-row--just-added').forEach((row) => {
      row.classList.remove('cart-items__table-row--just-added');
    });

    const returnFocusElement = this.#returnFocusElement;
    this.#returnFocusElement = null;

    if (returnFocusElement?.isConnected) {
      queueMicrotask(() => returnFocusElement.focus());
    }
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  /** @param {Event} [event] */
  open(event) {
    const trigger = event?.target;
    if (trigger instanceof HTMLElement) {
      this.#returnFocusElement = trigger;
    }

    const confirmation = this.querySelector('[data-cart-added-confirmation]');
    confirmation?.setAttribute('hidden', '');
    this.querySelectorAll('.cart-items__table-row--just-added').forEach((row) => {
      row.classList.remove('cart-items__table-row--just-added');
    });

    this.showDialog();
    this.#resetContentScroll();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
